import { currentLocale } from '@/lib/locale';
import { useState, useMemo } from 'react';
import { usePagination } from '@/hooks/usePagination';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  UserMinus,
  CheckCircle,
  Clock,
  AlertCircle,
  Search,
  Laptop,
  Info,
  RefreshCw,
  ShieldCheck,
  MoreHorizontal,
  ArrowRightLeft,
  Undo2,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { DeviceRecoveryPanel } from '@/components/inventory/DeviceRecoveryPanel';
import { BajaDecisionDialog, type BajaTarget } from '@/components/users/BajaDecisionDialog';
import { OffboardingReminderButton } from '@/components/users/OffboardingReminderButton';
import { formatDayMonth, getCurrentWeekRangeAR, isoDateInRange } from '@/../../shared/dates';

// Estado → chip (color + label). Función de `t`, no const de módulo: los labels
// salen de i18n y `t` solo existe dentro de un componente.
type TFunction = ReturnType<typeof useTranslation>['t'];
const STATE_CHIP = (t: TFunction): Record<string, { label: string; className: string }> => ({
  pendiente_decision: { label: t('admin.useroffboarding.pendiente_de_decision'), className: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' },
  transfiriendo: { label: t('admin.useroffboarding.transfiriendo_archivos'), className: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' },
  mantener: { label: t('admin.useroffboarding.mantenida'), className: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700' },
  completada: { label: t('admin.useroffboarding.dada_de_baja'), className: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800' },
  error: { label: 'Error', className: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800' },
  pendiente: { label: t('admin.useroffboarding.esperando_fecha'), className: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700' },
});

function StateChip({ estado, mensaje }: { estado: string; mensaje?: string | null }) {
  const { t } = useTranslation();
  const chips = STATE_CHIP(t);
  const chip = chips[estado] || chips.pendiente;
  return (
    <span
      title={mensaje || undefined}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${chip.className}`}
    >
      {chip.label}
    </span>
  );
}

function DeviceAlertButton({ correo, onClick }: { correo: string; onClick: () => void }) {
  const { t } = useTranslation();
  const { data: devices, isLoading } = trpc.inventory.getDevicesByOwner.useQuery(correo, {
    enabled: !!correo,
    staleTime: 5 * 60 * 1000,
  });
  if (isLoading) {
    return <span className="text-xs text-muted-foreground">…</span>;
  }
  // 'Vendida a Clapper' = la compró: no hay nada que recuperar
  const isSold = (s?: string) => (s ?? '').trim().toLowerCase() === 'vendida a clapper';
  const recoverable = (devices ?? []).filter((d) => !isSold(d.status));
  const soldCount = (devices ?? []).length - recoverable.length;

  if (recoverable.length === 0) {
    if (soldCount > 0) {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs text-muted-foreground"
          title={t('devices.recovery.sold_tooltip', { count: soldCount })}
        >
          <Laptop className="h-3.5 w-3.5" />
          {t('devices.recovery.sold_badge')}
        </span>
      );
    }
    return <span className="text-xs text-muted-foreground opacity-50">—</span>;
  }
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={onClick}
      title={soldCount > 0 ? t('devices.recovery.sold_tooltip', { count: soldCount }) : undefined}
      className="h-7 gap-1.5 border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:border-orange-700 dark:bg-orange-950/40 dark:text-orange-400"
    >
      <Laptop className="h-3.5 w-3.5" />
      <span className="text-xs font-semibold">{recoverable.length}</span>
    </Button>
  );
}

export function UserOffboardingPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [recoveryBaja, setRecoveryBaja] = useState<{ bajaId: string; correo: string; employeeName?: string; country?: string } | null>(null);
  const [decisionTarget, setDecisionTarget] = useState<BajaTarget | null>(null);

  const { data: sheetData, isLoading, error, refetch } = trpc.users.getBajasFromSheet.useQuery();

  const { data: communityRaw } = trpc.community.getMembers.useQuery({});
  const communityMembers = communityRaw?.data ?? [];
  const memberByEmail = useMemo(
    () => Object.fromEntries(communityMembers.map((m: any) => [m.email.toLowerCase(), m])),
    [communityMembers]
  );

  const decideMutation = trpc.users.decideBaja.useMutation({
    onSuccess: (_r, vars) => {
      toast.success(
        vars.decision === 'mantener'
          ? 'Cuenta mantenida'
          : 'Baja iniciada: suspendida y transfiriendo archivos'
      );
      setDecisionTarget(null);
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const revertMutation = trpc.users.revertBaja.useMutation({
    onSuccess: (r) => {
      toast.success(r.mensaje, { duration: 12000 });
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const syncMutation = trpc.users.triggerBajas.useMutation({
    onSuccess: () => {
      toast.success(t('admin.useroffboarding.bajas_actualizadas'));
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const allBajas = (sheetData || []) as any[];
  const stats = {
    pendienteDecision: allBajas.filter((b) => b.estado === 'pendiente_decision').length,
    transfiriendo: allBajas.filter((b) => b.estado === 'transfiriendo').length,
    completadas: allBajas.filter((b) => b.estado === 'completada').length,
    mantenidas: allBajas.filter((b) => b.estado === 'mantener').length,
  };

  const filteredBajas = allBajas.filter((baja) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return baja.correo?.toLowerCase().includes(q) || baja.id_baja?.toLowerCase().includes(q);
  });

  // Lista completa de la planilla montada de una: con cientos de filas
  // la página se hacía eterna.
  const { pageItems: pageBajas, footer: paginationFooter } = usePagination(filteredBajas, 25);

  const weekRange = useMemo(() => getCurrentWeekRangeAR(), []);
  const weekBajas = useMemo(
    () =>
      allBajas
        .filter((b) => isoDateInRange(b.fecha_baja_parsed, weekRange))
        .sort((a, b) => (a.fecha_baja_parsed || '').localeCompare(b.fecha_baja_parsed || '')),
    [allBajas, weekRange]
  );

  const handleDecide = (baja: BajaTarget) => decideMutation.mutate(baja);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('users.offboarding_page.title')}</h2>
          <p className="text-muted-foreground">{t('users.offboarding_page.description')}</p>
        </div>
        <Button variant="outline" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
          {syncMutation.isPending ? (
            <WinclapLoader size={16} className="mr-2" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          {t('admin.useroffboarding.actualizar_estados')}
        </Button>
      </div>

      <Tabs defaultValue="esta-semana">
        <TabsList>
          <TabsTrigger value="esta-semana">
            {t('users.week_tabs.this_week')}
            <span className="ml-1.5 rounded-full bg-muted-foreground/15 px-1.5 text-xs">{weekBajas.length}</span>
          </TabsTrigger>
          <TabsTrigger value="todos">{t('users.week_tabs.all')}</TabsTrigger>
        </TabsList>

        <TabsContent value="esta-semana" className="mt-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('users.week_tabs.week_of', {
              start: formatDayMonth(weekRange.weekStart),
              end: formatDayMonth(weekRange.weekEnd),
            })}
          </p>
          <Card>
            {isLoading ? (
              <div className="flex items-center justify-center p-12">
                <WinclapLoader size={48} className="text-foreground/25" />
              </div>
            ) : weekBajas.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 space-y-2">
                <UserMinus className="h-12 w-12 text-muted-foreground" />
                <p className="font-semibold">{t('users.week_tabs.empty_week_bajas')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('users.offboarding_page.table.exit_date')}</TableHead>
                      <TableHead>{t('users.offboarding_page.table.email')}</TableHead>
                      <TableHead>{t('admin.useroffboarding.pais')}</TableHead>
                      <TableHead>Clapbase</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Equipos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {weekBajas.map((baja) => {
                      const member = memberByEmail[baja.correo?.toLowerCase()];
                      return (
                        <TableRow key={baja.id_baja}>
                          <TableCell className="text-sm whitespace-nowrap">
                            {baja.fecha_baja_parsed ? formatDayMonth(baja.fecha_baja_parsed.slice(0, 10)) : baja.fecha_baja || '-'}
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{baja.correo}</p>
                            {member?.name && <p className="text-xs text-muted-foreground">{member.name}</p>}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{member?.country || '—'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{member?.clapbase || '—'}</TableCell>
                          <TableCell><StateChip estado={baja.estado} mensaje={baja.mensaje} /></TableCell>
                          <TableCell>
                            <DeviceAlertButton
                              correo={baja.correo}
                              onClick={() =>
                                setRecoveryBaja({ bajaId: baja.id_baja, correo: baja.correo, employeeName: member?.name, country: member?.country })
                              }
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="todos" className="mt-4 space-y-6">
      {/* Aviso del flujo */}
      <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-5">
        <div className="flex gap-3">
          <Info className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
          <div className="space-y-1.5 text-sm">
            <p className="font-semibold text-amber-900 dark:text-amber-200">{t('admin.useroffboarding.cada_baja_necesita_una_decision')}</p>
            <p className="text-amber-900/90 dark:text-amber-200/90">
              <Trans i18nKey="admin.useroffboarding.nada_se_elimina_explicacion" components={{ b: <strong /> }} />
            </p>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('admin.useroffboarding.pendientes_de_decision')}</p>
              <p className="text-2xl font-bold">{stats.pendienteDecision}</p>
            </div>
            <Clock className="h-8 w-8 text-amber-500" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Transfiriendo</p>
              <p className="text-2xl font-bold">{stats.transfiriendo}</p>
            </div>
            <ArrowRightLeft className="h-8 w-8 text-blue-500" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('admin.useroffboarding.dadas_de_baja')}</p>
              <p className="text-2xl font-bold">{stats.completadas}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Mantenidas</p>
              <p className="text-2xl font-bold">{stats.mantenidas}</p>
            </div>
            <ShieldCheck className="h-8 w-8 text-slate-500" />
          </div>
        </Card>
      </div>

      {/* Search */}
      <Card className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('users.offboarding_page.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Table */}
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <WinclapLoader size={48} className="text-foreground/25" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center p-12 space-y-4">
            <AlertCircle className="h-12 w-12 text-red-500" />
            <div className="text-center">
              <p className="font-semibold">{t('users.errors.load_data')}</p>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
          </div>
        ) : filteredBajas.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 space-y-4">
            <UserMinus className="h-12 w-12 text-muted-foreground" />
            <p className="font-semibold">{t('users.offboarding_page.no_data')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('users.offboarding_page.table.email')}</TableHead>
                  <TableHead>{t('admin.useroffboarding.pais')}</TableHead>
                  <TableHead>Clapbase</TableHead>
                  <TableHead>{t('users.offboarding_page.table.exit_date')}</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Equipos</TableHead>
                  <TableHead>Recordatorio</TableHead>
                  <TableHead className="text-right">{t('admin.useroffboarding.accion')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageBajas.map((baja) => {
                  const member = memberByEmail[baja.correo?.toLowerCase()];
                  const canDecide = baja.estado === 'pendiente_decision' || baja.estado === 'error';
                  const target = (decision: 'dar_de_baja' | 'mantener'): BajaTarget => ({
                    id_baja: baja.id_baja,
                    correo: baja.correo,
                    fecha_baja: baja.fecha_baja,
                    decision,
                  });
                  return (
                    <TableRow key={baja.id_baja}>
                      <TableCell>
                        <p className="font-medium">{baja.correo}</p>
                        {member?.name && <p className="text-xs text-muted-foreground">{member.name}</p>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{member?.country || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{member?.clapbase || '—'}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {baja.fecha_baja_parsed
                          ? new Date(baja.fecha_baja_parsed).toLocaleDateString(currentLocale())
                          : baja.fecha_baja || '-'}
                      </TableCell>
                      <TableCell><StateChip estado={baja.estado} mensaje={baja.mensaje} /></TableCell>
                      <TableCell>
                        <DeviceAlertButton
                          correo={baja.correo}
                          onClick={() =>
                            setRecoveryBaja({ bajaId: baja.id_baja, correo: baja.correo, employeeName: member?.name, country: member?.country })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <OffboardingReminderButton
                          baja={{
                            id_baja: baja.id_baja,
                            correo: baja.correo,
                            fecha_baja: baja.fecha_baja,
                            reminder_sent_at: baja.reminder_sent_at,
                          }}
                          memberName={member?.name}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        {canDecide ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-red-200 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
                              onClick={() => setDecisionTarget(target('dar_de_baja'))}
                            >
                              <UserMinus className="mr-1.5 h-3.5 w-3.5" />
                              {t('admin.useroffboarding.dar_de_baja')}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8"
                              onClick={() => setDecisionTarget(target('mantener'))}
                            >
                              Mantener
                            </Button>
                          </div>
                        ) : baja.estado === 'transfiriendo' ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <span className="text-xs text-muted-foreground">{t('admin.useroffboarding.se_completa_solo')}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              disabled={revertMutation.isPending}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `¿Revertir la baja de ${baja.correo}?\n\nSe reactiva la cuenta suspendida y el proceso queda detenido (no se vuelve a suspender ni eliminar).\n\nOjo: la transferencia de Drive ya iniciada NO se puede cancelar — si movió archivos, hay que devolverlos desde la cuenta backup.`
                                  )
                                ) {
                                  revertMutation.mutate({ id_baja: baja.id_baja, correo: baja.correo });
                                }
                              }}
                            >
                              <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                              Revertir
                            </Button>
                          </div>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" aria-label={t('common.a11y.more_actions')} className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => setDecisionTarget(target('dar_de_baja'))}
                              >
                                <UserMinus className="mr-2 h-4 w-4" /> {t('admin.useroffboarding.dar_de_baja')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setDecisionTarget(target('mantener'))}
                              >
                                <ShieldCheck className="mr-2 h-4 w-4" /> Mantener
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {paginationFooter && <div className="px-4 pb-3">{paginationFooter}</div>}
          </div>
        )}
      </Card>
        </TabsContent>
      </Tabs>

      {recoveryBaja && (
        <DeviceRecoveryPanel
          open={!!recoveryBaja}
          onClose={() => setRecoveryBaja(null)}
          bajaId={recoveryBaja.bajaId}
          correo={recoveryBaja.correo}
          employeeName={recoveryBaja.employeeName}
          country={recoveryBaja.country}
        />
      )}

      <BajaDecisionDialog
        target={decisionTarget}
        onClose={() => setDecisionTarget(null)}
        onConfirm={handleDecide}
        pending={decideMutation.isPending}
      />
    </div>
  );
}
